import type { ApplicationSettingsTabId } from '../../../store/application-settings-tab-id';

export type TargetSettingsNavGroupId =
  | 'general'
  | 'source-code'
  | 'testing-scope'
  | 'networking'
  | 'credentials'
  | 'automation'
  | 'business-context'
  | 'testing-controls'
  | 'copilot-skills'
  | 'prevention'
  | 'mcp-servers'
  | 'mcp-connection'
  | 'danger';

type TargetSettingsNavSection = {
  value: ApplicationSettingsTabId;
  labelKey: string;
  tooltipKey?: string;
};

export type TargetSettingsNavGroup = {
  value: TargetSettingsNavGroupId;
  labelKey: string;
  sections: readonly TargetSettingsNavSection[];
};

export const TARGET_SETTINGS_DEFAULT_SECTION: ApplicationSettingsTabId =
  'general';

const singleSectionGroup = ({
  value,
  section,
  labelKey,
}: {
  value: TargetSettingsNavGroupId;
  section: ApplicationSettingsTabId;
  labelKey: string;
}): TargetSettingsNavGroup => ({
  value,
  labelKey,
  sections: [{ value: section, labelKey }],
});

export const TARGET_SETTINGS_NAV_GROUPS: readonly TargetSettingsNavGroup[] = [
  singleSectionGroup({
    value: 'general',
    section: 'general',
    labelKey: 'general',
  }),
  singleSectionGroup({
    value: 'source-code',
    section: 'source-code',
    labelKey: 'sourceCode',
  }),
  {
    value: 'testing-scope',
    labelKey: 'testingScope',
    sections: [
      { value: 'domains', labelKey: 'domains', tooltipKey: 'domainsTooltip' },
      {
        value: 'signal-categories',
        labelKey: 'signalCategories',
        tooltipKey: 'signalCategoriesTooltip',
      },
      { value: 'deployment-delay', labelKey: 'deploymentDelay' },
    ],
  },
  singleSectionGroup({
    value: 'networking',
    section: 'networking',
    labelKey: 'networking',
  }),
  singleSectionGroup({
    value: 'mcp-connection',
    section: 'mcp-connection',
    labelKey: 'mcpConnection',
  }),
  {
    value: 'credentials',
    labelKey: 'credentials',
    sections: [
      { value: 'credentials', labelKey: 'credentials' },
      { value: 'roles', labelKey: 'roles' },
    ],
  },
  singleSectionGroup({
    value: 'automation',
    section: 'automation',
    labelKey: 'automation',
  }),
  {
    value: 'business-context',
    labelKey: 'businessContext',
    sections: [
      { value: 'documents', labelKey: 'documentation' },
      { value: 'knowledge-sources', labelKey: 'knowledgeSources' },
      { value: 'scoping', labelKey: 'scoping' },
      { value: 'cvss', labelKey: 'cvss' },
    ],
  },
  {
    value: 'testing-controls',
    labelKey: 'testingControls',
    sections: [
      { value: 'guardrails', labelKey: 'guardrails' },
      { value: 'instructions', labelKey: 'instructions' },
      { value: 'memories', labelKey: 'memories' },
    ],
  },
  singleSectionGroup({
    value: 'prevention',
    section: 'prevention',
    labelKey: 'prevention',
  }),
  singleSectionGroup({
    value: 'mcp-servers',
    section: 'mcp-servers',
    labelKey: 'mcpServers',
  }),
  singleSectionGroup({
    value: 'copilot-skills',
    section: 'copilot-skills',
    labelKey: 'copilotSkills',
  }),
  singleSectionGroup({
    value: 'danger',
    section: 'danger',
    labelKey: 'danger',
  }),
];

export type TargetSettingsNavVisibility = {
  isAiApplicationTarget: boolean;
  isMcpServersEnabled: boolean;
  hasApplicationEligibleMcpServers: boolean;
  isMcpServerTarget: boolean;
  hasAppTargetId: boolean;
  isPreventionEnabled: boolean;
  canDeleteApplication: boolean;
};

const isSectionVisible = ({
  section,
  visibility,
}: {
  section: ApplicationSettingsTabId;
  visibility: TargetSettingsNavVisibility;
}): boolean => {
  if (section === 'prevention') {
    return visibility.isPreventionEnabled && !visibility.isAiApplicationTarget;
  }

  if (section === 'memories') {
    return !visibility.isAiApplicationTarget;
  }

  if (section === 'danger') {
    return visibility.canDeleteApplication;
  }

  if (section === 'mcp-servers') {
    return (
      visibility.isMcpServersEnabled &&
      visibility.hasApplicationEligibleMcpServers
    );
  }

  if (section === 'mcp-connection') {
    return visibility.isMcpServerTarget && visibility.hasAppTargetId;
  }

  return true;
};

export const getVisibleTargetSettingsNavGroups = (
  visibility: TargetSettingsNavVisibility,
): TargetSettingsNavGroup[] =>
  TARGET_SETTINGS_NAV_GROUPS.map((group) => ({
    ...group,
    sections: group.sections.filter((section) =>
      isSectionVisible({ section: section.value, visibility }),
    ),
  })).filter((group) => group.sections.length > 0);

export const resolveVisibleTargetSettingsSection = ({
  activeSection,
  visibility,
}: {
  activeSection: ApplicationSettingsTabId;
  visibility: TargetSettingsNavVisibility;
}): ApplicationSettingsTabId =>
  findTargetSettingsNavGroup({
    groups: TARGET_SETTINGS_NAV_GROUPS,
    section: activeSection,
  }) !== undefined && isSectionVisible({ section: activeSection, visibility })
    ? activeSection
    : TARGET_SETTINGS_DEFAULT_SECTION;

export const findTargetSettingsNavGroup = ({
  groups,
  section,
}: {
  groups: readonly TargetSettingsNavGroup[];
  section: ApplicationSettingsTabId;
}): TargetSettingsNavGroup | undefined =>
  groups.find((group) =>
    group.sections.some((entry) => entry.value === section),
  );

export const resolveTargetSettingsGroupEntrySection = ({
  groups,
  groupId,
}: {
  groups: readonly TargetSettingsNavGroup[];
  groupId: string;
}): ApplicationSettingsTabId | undefined =>
  groups.find((group) => group.value === groupId)?.sections[0]?.value;
